/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { FiRefreshCw } from "react-icons/fi";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import PageContainer from "../components/PageContainer";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";

export default function AdminActivityLog() {
  const { theme, lang, colors } = useUI();
  const isArabic = lang === "ar";
  const isDark = theme === "dark";
  const perms = useTenantStorePermissions();

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api")
    .replace(/\/+$/, "");

  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivityLogs();
  }, []);

  const fetchActivityLogs = async () => {
    if (!perms.viewActivity) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const endpoint = `${baseUrl}/ActivityLog/GetLogs?tenantId=${tenantId}`;
      console.log("📥 Fetching activity logs:", endpoint);

      const res = await fetch(endpoint, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("📥 Raw logs:", data);

      if (!Array.isArray(data)) {
        setActivities([]);
        return;
      }

      const mappedActivities = data.map((log) => ({
        id: log.logId,
        action: log.action || "—",
        user: log.username && log.username !== "Unknown" ? log.username : "—",
        target: log.controller || "—",
        date: log.createDate,
      }));

      setActivities(mappedActivities);
      console.log("✅ Logs loaded:", mappedActivities.length);
    } catch (err) {
      console.error("❌ GetLogs error:", err);

      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحميل ❌" : "Load Failed ❌",
        text: err.message,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });

      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchActivityLogs();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const h = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");

    return `${y}-${m}-${d} ${h}:${min}`;
  };

  const translateAction = (action) => {
    if (!action) return "—";
    const a = action.toLowerCase();

    if (a.includes("get")) return isArabic ? "عرض" : "Get";
    if (a.includes("add") || a.includes("create")) return isArabic ? "إضافة" : "Add";
    if (a.includes("edit") || a.includes("update")) return isArabic ? "تعديل" : "Edit";
    if (a.includes("delete")) return isArabic ? "حذف" : "Delete";
    if (a.includes("login")) return isArabic ? "تسجيل دخول" : "Login";
    if (a.includes("logout")) return isArabic ? "تسجيل خروج" : "Logout";

    return action;
  };

  const translateTarget = (controller) => {
    if (!controller) return "—";

    const map = {
      Orders: isArabic ? "الطلبات" : "Orders",
      Products: isArabic ? "المنتجات" : "Products",
      Items: isArabic ? "المنتجات" : "Items",
      Categories: isArabic ? "الأقسام" : "Categories",
      TenantCategory: isArabic ? "الأقسام" : "Categories",
      Banners: isArabic ? "البنرات" : "Banners",
      Coupons: isArabic ? "الكوبونات" : "Coupons",
      Customers: isArabic ? "العملاء" : "Customers",
      Users: isArabic ? "المستخدمين" : "Users",
      TenantUser: isArabic ? "الموظفين" : "Employees",
      ActivityLog: isArabic ? "سجل النشاط" : "Activity Log",
      Settings: isArabic ? "الإعدادات" : "Settings",
      Payments: isArabic ? "المدفوعات" : "Payments",
      Delivery: isArabic ? "التوصيل" : "Delivery",
      Tags: isArabic ? "الوسوم" : "Tags",
    };

    return map[controller] || controller;
  };

  if (!perms.viewActivity) {
    return <NoAccess />;
  }

  if (loading) {
    return (
      <PageContainer title={isArabic ? "سجل الإجراءات" : "Activity Log"}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7400BF]" />
            <p className="mt-4 text-gray-500">
              {isArabic ? "جاري التحميل..." : "Loading..."}
            </p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={isArabic ? "سجل الإجراءات" : "Activity Log"}>
      <ResponsiveTable
        columns={[
          { header: isArabic ? "الإجراء" : "Action", sortKey: "action", align: "center" },
          { header: isArabic ? "المستخدم" : "User", sortKey: "user", align: "center" },
          { header: isArabic ? "الهدف" : "Target", sortKey: "target", align: "center" },
          { header: isArabic ? "التاريخ" : "Date", sortKey: "date", align: "center" },
        ]}
        data={activities}
        searchable
        sortable
        headerActions={
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium shadow-md active:scale-[1.03] transition"
            style={{ background: colors.mainColor }}
          >
            <FiRefreshCw />
            {isArabic ? "تحديث" : "Refresh"}
          </button>
        }
        renderRow={(a) => (
          <>
            <td className="py-3 px-4 text-center">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${a.action.toLowerCase().includes("get")
                    ? "bg-blue-500/10 text-blue-400"
                    : a.action.toLowerCase().includes("add") || a.action.toLowerCase().includes("create")
                      ? "bg-green-500/10 text-green-400"
                      : a.action.toLowerCase().includes("edit") || a.action.toLowerCase().includes("update")
                        ? "bg-yellow-500/10 text-yellow-400"
                        : a.action.toLowerCase().includes("delete")
                          ? "bg-red-500/10 text-red-400"
                          : "bg-gray-500/10 text-gray-400"
                  }`}
              >
                {translateAction(a.action)}
              </span>
            </td>

            <td className="py-3 px-4 text-center font-medium">{a.user}</td>

            <td className="py-3 px-4 text-center font-medium">
              {translateTarget(a.target)}
            </td>

            <td className="py-3 px-4 text-center text-gray-500">
              {formatDate(a.date)}
            </td>
          </>
        )}
      />
    </PageContainer>
  );
}
